import { QueryErrorResetBoundary, useQueryErrorResetBoundary } from '@tanstack/react-query';
import { useSuspenseQueryOperation } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { ReactNode } from 'react';
import { createContext, type JSX, use, useState } from 'react';
import type { Callback } from 'ts/base/Callback';
import { useNavigationHash } from 'ts/base/hooks/UseNavigationHash';
import { useProjectId } from 'ts/base/hooks/UseProject';
import type { SortOptions } from 'ts/base/table/SortableTable';
import { useLocalStorage } from 'ts/commons/hooks/UseLocalStorage';
import type { IssueLikeQueryableEntityType } from 'ts/commons/query/QueryableEntityType';
import { ESortOrder } from 'typedefs/ESortOrder';

/** The "public" API of the filter options. */
type ExposedIssueQueryContext = {
	/** Toggles the filter from basic filter to custom query and back. */
	setActiveQuery: Callback<string>;
	/** Overwrites the given options in the current filter state. */
	setCurrentQuery: Callback<string>;
	/** The type of the issue query (spec item or issue) */
	issueLikeType: IssueLikeQueryableEntityType;
	/** Holds the content of the issue query text field. The query might not have been submitted yet. */
	currentQuery: string;
	/** Points to the current active query that that data should be shown for (e.g. 'status = Open'). */
	activeQuery: string;
	sortOptions: SortOptions;
	setSortOptions: Callback<SortOptions>;
	visibleColumns: string[];
	setVisibleColumns: Callback<string[]>;
	commit?: UnresolvedCommitDescriptor;
};

/** Context that holds the current state of the test filter. */
const IssueQueryContext = createContext<ExposedIssueQueryContext | undefined>(undefined);

/** Props for IssueQueryContextProvider. */
type IssueQueryContextProviderProps = {
	issueLikeType: IssueLikeQueryableEntityType;
	children: ReactNode;
	activeQuery: string;
	onQuerySubmit?: Callback<string>;
	commit?: UnresolvedCommitDescriptor | undefined;
};

/** Context provider for the issue query. */
export function IssueQueryContextProvider({ children, ...props }: IssueQueryContextProviderProps): JSX.Element {
	// Everytime we use the issue query (thus this provider), we want to create our own QueryErrorResetBoundary for it
	// So wrap the actual provider into a QueryErrorResetBoundary, so that the caller doesn't have to
	return (
		<QueryErrorResetBoundary>
			<IssueQueryContextProviderInternal {...props}>{children}</IssueQueryContextProviderInternal>
		</QueryErrorResetBoundary>
	);
}

/** Actual implementation of the IssueQueryContextProvider. */
function IssueQueryContextProviderInternal({
	issueLikeType,
	activeQuery,
	onQuerySubmit,
	children,
	commit
}: IssueQueryContextProviderProps): JSX.Element {
	const hash = useNavigationHash();
	const { reset } = useQueryErrorResetBoundary();
	const [currentQuery, setCurrentQuery] = useState(activeQuery);
	const [sortOptions, setSortOptions] = useState<SortOptions>({
		sortByField: 'id',
		sortOrder: ESortOrder.DESCENDING
	});
	const [visibleColumns, setVisibleColumns] = useLocalStorage(
		'visible-columns-' + issueLikeType.lowerCasedName + '-' + hash.getProject(),
		issueLikeType.defaultColumns
	);
	const value: ExposedIssueQueryContext = {
		issueLikeType,
		activeQuery,
		setActiveQuery(query) {
			reset();
			onQuerySubmit?.(query);
		},
		currentQuery,
		setCurrentQuery,
		sortOptions,
		setSortOptions,
		visibleColumns,
		setVisibleColumns,
		commit
	};
	return <IssueQueryContext value={value}>{children}</IssueQueryContext>;
}

/**
 * Returns the current test filter settings and exposes some functions to update the filter. Must only be used in
 * components nested within a TestFilterOptionsProvider.
 */
export function useIssueQueryContext(): ExposedIssueQueryContext {
	const context = use(IssueQueryContext);
	if (context === undefined) {
		throw new Error('useIssueQueryContext must be used within a TestFilterOptionsProvider');
	}
	return context;
}

/** Exposes the overall number of the given issue-like entities that are known for the current project. */
export function useIssueCount(issueLikeType: IssueLikeQueryableEntityType, commit: UnresolvedCommitDescriptor): number {
	const projectId = useProjectId();
	return useSuspenseQueryOperation(issueLikeType.getOverallCount(projectId, commit));
}
